import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { useNavigate } from 'react-router';
import { PATH_DASHBOARD } from 'routes/paths';
import { TABLE_ROW_COUNT } from 'utils/constants';
import { decodeErrorMessage } from 'utils/helper';
import API from '../../API';

const fetchStaffData = async ({ pageParam = 1, search = '', status = '', limit = false }) => {
  try {
    const rowLimit = Number(limit) > 0 ? Number(limit) : TABLE_ROW_COUNT;

    const response = await API.get(
      `/project-panel/staff-management/get-all-staff?page=${pageParam}&limit=${rowLimit}&search=${search}&status=${status}`
    );
    const data = response.data?.data;

    const totalPages = data?.counts?.pages || 1;
    const nextPage = totalPages > pageParam ? pageParam + 1 : null;
    const prevPage = pageParam > 1 ? pageParam - 1 : null;
    return { data, nextPage, prevPage };
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error fetching data');
  }
};

export const useGetAllStaff = (props) => {
  const { search, status, limit = false, endOnLastPage = false } = props;
  const rowLimit = Number(limit) > 0 ? Number(limit) : TABLE_ROW_COUNT;

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery(
    ['_getAllStaff', search, status, rowLimit, endOnLastPage],
    async ({ pageParam = 1, queryKey }) => {
      /* eslint-disable */
      return await fetchStaffData({ pageParam, search, status, limit });
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.nextPage ? lastPage.nextPage : endOnLastPage ? undefined : 1,
      getPreviousPageParam: (lastPage) =>
        lastPage.prevPage ? lastPage.prevPage : endOnLastPage ? undefined : 1,
    }
  );

  /* eslint-disable */
  const lastIndex = data?.pages?.length - 1 || 0;

  return {
    data: {
      data: data?.pages?.flatMap((newData) => newData.data.data),
      counts: data?.pages[lastIndex]?.data?.counts,
    },
    isLoading,
    isError,
    error,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    isFetchingNextPage,
    isFetching,
  };
};

export const useCreateNewStaff = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate, isLoading } = useMutation(
    (payload) => API.post('/project-panel/staff-management/create-staff', payload),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['_getAllStaff']);
        enqueueSnackbar(response?.data?.message || 'success');
        navigate(PATH_DASHBOARD.staffManagement.list);
      },
      onError: (error) => {
        const errorMessage = decodeErrorMessage(error);
        enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
      },
    }
  );
  return {
    mutate,
    isLoading,
  };
};

export const useUpdateStaff = ({ id = '' }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate, isLoading } = useMutation(
    (payload) => API.put(`/project-panel/staff-management/update-staff-by-id/${id}`, payload),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['_getAllStaff', id]);
        enqueueSnackbar(response?.data?.message || 'success');
        navigate(PATH_DASHBOARD.staffManagement.list);
      },
      onError: (error) => {
        const errorMessage = decodeErrorMessage(error);
        enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
      },
    }
  );
  return {
    mutate,
    isLoading,
  };
};
export const useUpdateStaffPassword = ({ id = '' }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate, isLoading } = useMutation(
    (payload) => API.put(`/project-panel/staff-management/update-staff-password-by-id/${id}`, payload),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['_getAllStaff', id]);
        enqueueSnackbar(response?.data?.message || 'success');
        navigate(PATH_DASHBOARD.staffManagement.list);
      },
      onError: (error) => {
        const errorMessage = decodeErrorMessage(error);
        enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
      },
    }
  );
  return {
    mutate,
    isLoading,
  };
};
export const useDeleteStaffById = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation(
    (id) => API.delete(`/project-panel/staff-management/delete-staff-by-id/${id}`),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['_getAllStaff']);
        enqueueSnackbar(response?.data?.message || 'success');
      },
      onError: (error) => {
        const errorMessage = decodeErrorMessage(error);
        enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
        console.log(error);
      },
    }
  );
  return {
    mutate,
    isLoading,
  };
};

export const useGetStaffById = (id) => {
  const { data, isError, isLoading } = useQuery(['_getAllStaff', id], () =>
    API.get(`/project-panel/staff-management/get-staff-by-id/${id}`)
  );

  return {
    data: data?.data?.data || {},
    isLoading,
    isError,
  };
};

export const useUpdateStaffKanbanOrder = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation(
    async (items) => API.put('/project-panel/staff-management/update-staff-kanban-order', { items }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['_getAllStaff']);
      },
      onError: (error) => {
        queryClient.invalidateQueries(['_getAllStaff']);
        enqueueSnackbar(decodeErrorMessage(error), { variant: 'error' });
      },
    }
  );

  return { mutate, isLoading };
};

export const useUpdateStaffStatus = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate, isLoading } = useMutation(
    async (payload) =>
      await API.put(`/project-panel/staff-management/update-staff-status/${payload.id}`, payload),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['_getAllStaff']);
        enqueueSnackbar(response?.data?.message || 'success');
      },
      onError: (error) => {
        const errorMessage = decodeErrorMessage(error);
        enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
        console.log(error);
      },
    }
  );
  return {
    mutate,
    isLoading,
  };
};

export const useCheckStaffEmailOrMobileExistOrNot = () => {
  const { mutate, isLoading } = useMutation(
    async (payload) =>
      await API.post('/project-panel/staff-management/check-staff-email-or-mobile-exist', payload)
  );
  return {
    mutate,
    isLoading,
  };
};
export const useGetAllStaffModules = () => {
  const { data, isError, isLoading } = useQuery(['_getAllStaffModules'], () =>
    API.get(`/project-panel/staff-management/get-all-staff-modules`)
  );

  return {
    data: data?.data?.data || {},
    isLoading,
    isError,
  };
};
export const useGetAllStaffWebsiteAccounts = () => {
  const { data, isError, isLoading } = useQuery(['_getAllStaffWebsiteAccounts'], () =>
    API.get(`/project-panel/auth/get-user-website-accounts`)
  );

  return {
    data: data?.data?.data || {},
    isLoading,
    isError,
  };
};
export const useGetAllStaffWebsiteChildAccounts = () => {
  const { data, isError, isLoading } = useQuery(['_getAllStaffWebsiteChildAccounts'], () =>
    API.get(`/project-panel/auth/get-user-child-website-accounts`)
  );

  return {
    data: data?.data?.data || {},
    isLoading,
    isError,
  };
};